import * as React from 'react';

import {
  enumPurchaseLoadStatus,
  PurchaseLoadStatus,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { $columnSelectedInboundStatuses } from '../../../_state/transactions.state';
import { useTranslations } from '../../../i18n/useTranslations';
import {
  purchaseLoadStatusColors,
  purchaseLoadStatusTextMap,
} from '../../../utils/status';

import { FilterOptionLabel } from './FilterOptionLabel';

interface StatusOption {
  id: PurchaseLoadStatus;
  label: string;
}

interface InboundStatusColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ INBOUND STATUS COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const InboundStatusColumnFilter: React.FC<
  InboundStatusColumnFilterProps
> = ({ onClose }) => {
  const t = useTranslations();

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Atoms
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedInboundStatuses,
  );
  const selectedStatuses = Array.from(selectedStatusesSet);

  // Memoized values
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  const statusOptions: StatusOption[] = React.useMemo(
    () =>
      Object.values(enumPurchaseLoadStatus).map((status) => ({
        id: status,
        label: purchaseLoadStatusTextMap[status](t),
      })),
    [t],
  );

  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(new Set(Array.from(keys) as PurchaseLoadStatus[]));
    },
    [setSelectedStatusesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <FilterOptionLabel
        label={item.label}
        badge={
          <span
            className={cn(
              'size-2 rounded-full',
              purchaseLoadStatusColors[item.id],
            )}
          />
        }
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@InboundStatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.transactionsView.filters.status.searchPlaceholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
